import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import apiRoutesV2, { baseURL } from 'src/config/api/routes';
import { RootState } from 'src/modules/shared/store';

export type GetStudentBookmark = {
  bookId: string;
  chapterId: string;
  trajectoryId: string;
  bookmarkId: string;
  id: string;
  blockId: string;
  section: string;
  pageId: string;
  title: string;
  path: string;
  fromMyTeacher?: boolean;
};

export type GetTeachersBookmarkByBook = {
  groupId: string;
  groupName: string;
  groupIsDefault: string;
  bookId: string;
  chapterId: string;
  trajectoryId: string;
  bookmarkId: string;
  id: string;
  blockId: string;
  section: string;
  pageId: string;
  isTeacherOnly: boolean;
};

export type AddStudentBookmarkRequest = {
  bookId: string;
  blockId: string;
};

export type AddTeacherGroupBookmarkRequestGroup = {
  group_id: string;
  enabled: boolean;
};

export type AddTeacherGroupBookmarkRequestBody = {
  data: AddTeacherGroupBookmarkRequestGroup[];
  isTeacherOnly?: boolean;
};

export type AddTeacherGroupBookmarkRequest = {
  bookId: string;
  blockId: string;
  body: AddTeacherGroupBookmarkRequestBody;
};

export const bookmarkApi = createApi({
  reducerPath: 'bookmarkApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getStudentBookmark: builder.query<GetStudentBookmark[], string>({
      query: (bookId: string) => ({
        url: apiRoutesV2.studentTrajectoryBookmark(bookId),
        method: 'GET',
      }),
      transformResponse: (response: { data: GetStudentBookmark[] }) => {
        return response.data;
      },
    }),
    getTeacherBookmarksByBook: builder.query<GetTeachersBookmarkByBook[], string>({
      query: (bookId: string) => ({
        url: apiRoutesV2.getTeacherBookmarksForBook(bookId),
        method: 'GET',
      }),
      transformResponse: (response: { data: GetTeachersBookmarkByBook[] }) => {
        return response.data;
      },
    }),
    addStudentBookmark: builder.mutation<void, AddStudentBookmarkRequest>({
      query: ({ bookId, blockId }) => ({
        url: apiRoutesV2.postStudentTrajectoryBookmark(bookId, blockId),
        method: 'POST',
      }),
    }),
    addTeacherGroupBookmark: builder.mutation<void, AddTeacherGroupBookmarkRequest>({
      query: ({ bookId, blockId, body }) => ({
        url: apiRoutesV2.postTeacherBookmark(bookId, blockId),
        method: 'POST',
        body: body,
      }),
    }),
  }),
});

export const {
  useGetStudentBookmarkQuery,
  useGetTeacherBookmarksByBookQuery,
  useAddStudentBookmarkMutation,
  useAddTeacherGroupBookmarkMutation,
} = bookmarkApi;
